import React, { useEffect, useMemo, useState } from 'react';

import { Button, message, Popconfirm, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { api } from '../../http/API';
import { cultureAPI } from '../../http/CultureAPI';
import { UserCulture } from '../../models/Culture';
import { Field } from '../../models/Field';
import { FieldCropHistory } from '../../models/FieldCropHistory';

import FieldSeasonModal from '../fields/components/FieldSeasonModal';

interface SeasonRow extends FieldCropHistory {
    field_id: number;
    field_name: string;
}

const STATUS_LABELS: Record<string, string> = {
    planned: 'Запланирован',
    sown: 'Посеян',
    harvested: 'Убран',
};

const SeasonsPage = () => {
    const [fields, setFields] = useState<Field[]>([]);
    const [seasons, setSeasons] = useState<SeasonRow[]>([]);
    const [myCultures, setMyCultures] = useState<UserCulture[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalFieldId, setModalFieldId] = useState<number | null>(null);
    const [modalSeason, setModalSeason] = useState<FieldCropHistory | null>(null);

    const [addFieldId, setAddFieldId] = useState<number | undefined>(undefined);

    const loadAll = () => {
        setLoading(true);
        Promise.all([
            api.GetFields(),
            cultureAPI.GetMyCultures().catch(() => [] as UserCulture[]),
        ])
            .then(([fieldsData, culturesData]) => {
                const list = fieldsData || [];
                setFields(list);
                setMyCultures(culturesData || []);
                return Promise.all(
                    list.map((f) =>
                        api
                            .GetFieldSeasons(f.id as number)
                            .then((rows) => ({ field: f, rows: rows || [] }))
                            .catch(() => ({ field: f, rows: [] as FieldCropHistory[] })),
                    ),
                );
            })
            .then((pairs) => {
                const flat: SeasonRow[] = [];
                pairs.forEach(({ field, rows }) => {
                    rows.forEach((s) =>
                        flat.push({
                            ...s,
                            field_id: field.id as number,
                            field_name: field.name || `Поле #${field.id}`,
                        }),
                    );
                });
                flat.sort((a, b) => {
                    if (a.field_id !== b.field_id) return a.field_id - b.field_id;
                    return b.season_harvest - a.season_harvest;
                });
                setSeasons(flat);
            })
            .catch(() => message.error('Не удалось загрузить сезоны'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAll();
    }, []);

    const cultureLabel = (s: FieldCropHistory): string => {
        const uc = myCultures.find(
            (c) => c.culture_id === s.culture_id && (c.sort_id ?? null) === (s.sort_id ?? null),
        );
        if (uc) {
            return uc.sort ? `${uc.culture?.name || ''} — ${uc.sort.name}` : uc.culture?.name || '';
        }
        return `Культура #${s.culture_id}${s.sort_id ? ` — сорт #${s.sort_id}` : ''}`;
    };

    const openAdd = () => {
        if (!addFieldId) {
            message.warning('Выберите поле');
            return;
        }
        setModalFieldId(addFieldId);
        setModalSeason(null);
        setModalOpen(true);
    };

    const openEdit = (row: SeasonRow) => {
        setModalFieldId(row.field_id);
        setModalSeason(row);
        setModalOpen(true);
    };

    const handleDelete = (row: SeasonRow) => {
        api.DeleteFieldSeason(row.field_id, row.season_harvest)
            .then(() => {
                message.success('Сезон удалён');
                loadAll();
            })
            .catch(() => message.error('Не удалось удалить сезон'));
    };

    const columns: ColumnsType<SeasonRow> = useMemo(
        () => [
            { title: 'Поле', dataIndex: 'field_name', key: 'field_name' },
            { title: 'Сезон', dataIndex: 'season_harvest', key: 'season_harvest', width: 90 },
            {
                title: 'Культура',
                key: 'culture',
                render: (_, row) => cultureLabel(row),
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 130,
                render: (v: string) => STATUS_LABELS[v] || v,
            },
            { title: 'Посев', dataIndex: 'sowing_date', key: 'sowing_date', width: 120 },
            { title: 'Уборка', dataIndex: 'harvest_date', key: 'harvest_date', width: 120 },
            {
                title: 'Урожайность',
                dataIndex: 'actual_yield',
                key: 'actual_yield',
                width: 130,
                render: (v: number | null) => (v != null ? `${v} ц/га` : '—'),
            },
            {
                title: '',
                key: 'actions',
                width: 200,
                render: (_, row) => (
                    <Space>
                        <Button size="small" onClick={() => openEdit(row)}>
                            Изменить
                        </Button>
                        <Popconfirm
                            title="Удалить сезон?"
                            okText="Да"
                            cancelText="Нет"
                            onConfirm={() => handleDelete(row)}
                        >
                            <Button size="small" danger>
                                Удалить
                            </Button>
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [myCultures],
    );

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Select
                    placeholder="Выберите поле"
                    style={{ minWidth: 260 }}
                    value={addFieldId}
                    onChange={setAddFieldId}
                    showSearch
                    optionFilterProp="children"
                    options={fields.map((f) => ({
                        value: f.id,
                        label: `${f.name || 'Поле'} (#${f.id})`,
                    }))}
                />
                <Button type="primary" onClick={openAdd}>
                    Добавить сезон
                </Button>
            </div>

            <Table
                rowKey={(r) => `${r.field_id}_${r.season_harvest}`}
                columns={columns}
                dataSource={seasons}
                loading={loading}
                pagination={{ pageSize: 20 }}
                size="small"
                locale={{ emptyText: 'Сезонов пока нет' }}
            />

            {modalFieldId !== null && (
                <FieldSeasonModal
                    open={modalOpen}
                    fieldId={modalFieldId}
                    season={modalSeason}
                    onCancel={() => {
                        setModalOpen(false);
                        setModalSeason(null);
                    }}
                    onSaved={() => {
                        setModalOpen(false);
                        setModalSeason(null);
                        loadAll();
                    }}
                />
            )}
        </div>
    );
};

export default SeasonsPage;
